import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Select, MenuItem, FormControl, InputLabel, Grid, Paper, Container,
  Card, CardContent, Chip, IconButton, Tooltip, Autocomplete, Grow, Avatar, Divider, Stack
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Haversine formula to calculate distance between two points in km
function calculateDistance(lat1, lon1, lat2, lon2) {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2)
    ; 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  const d = R * c; // Distance in km
  return d;
}

function deg2rad(deg) {
  return deg * (Math.PI/180);
}

const WORK_TYPES = [
  { category: 'CRM & Office', title: 'Follow-up' },
  { category: 'Welding NDT Inspection Services', title: 'QA/QC Mechanical Inspector' },
  { category: 'Welding NDT Inspection Services', title: 'Welding Inspector (CSWIP 3.1)' },
  { category: 'Welding NDT Inspection Services', title: 'NDT Level III Services' },
  { category: 'Welding NDT Inspection Services', title: 'Visual Inspection VT' },
  { category: 'Welding NDT Inspection Services', title: 'Radiographic Testing (XRAY Technic)' },
  { category: 'Welding NDT Inspection Services', title: 'RTFI Inspector' },
  { category: 'Welding NDT Inspection Services', title: 'Dye Penetrant Testing (DPT)' },
  { category: 'Welding NDT Inspection Services', title: 'Magnetic Particle Testing (MPT)' },
  { category: 'Welding NDT Inspection Services', title: 'Ultrasonic Testing (UT)' },
  { category: 'Welding NDT Inspection Services', title: 'Fit up Inspection' },
  { category: 'Welding NDT Inspection Services', title: 'Post Weld Heat Treatment (PWHT)' },

  { category: 'Steel NDT Services', title: 'Steel Structure Condition Assesment' },
  { category: 'Steel NDT Services', title: 'Ultrasonic Thickness Test (UTT)' },
  { category: 'Steel NDT Services', title: 'Dimentional Test (DT)' },
  { category: 'Steel NDT Services', title: 'Manual Bolt Tightening Test (Manual Torque Wrenches)' },
  { category: 'Steel NDT Services', title: 'Digital Bolt Tightening Test (Digital Torque Measurment)' },
  { category: 'Steel NDT Services', title: 'Hardness Test (HT)' },
  { category: 'Steel NDT Services', title: 'Ferrite Test (FT)' },
  { category: 'Steel NDT Services', title: 'Positive Material Identification (PMI)' },
  { category: 'Steel NDT Services', title: 'Shear Stud Test' },
  { category: 'Steel NDT Services', title: 'Macro Etching Test' },

  { category: 'Painting NDT Inspection Services', title: 'Surface Preparation Services' },
  { category: 'Painting NDT Inspection Services', title: 'Roughness Test' },
  { category: 'Painting NDT Inspection Services', title: 'Dust Contamination Test' },
  { category: 'Painting NDT Inspection Services', title: 'Wet Film Thickness (WFT)' },
  { category: 'Painting NDT Inspection Services', title: 'Dry Film Thickness Test (DFT)' },
  { category: 'Painting NDT Inspection Services', title: 'Coating Holiday Test' },
  { category: 'Painting NDT Inspection Services', title: 'Painting Inspector (BGAS)' },

  { category: 'Welder Certification & Qualification', title: 'Preparing / Revising WPS' },
  { category: 'Welder Certification & Qualification', title: 'NDT & WPQR Testing & Records' },

  { category: 'NDT Special Testing', title: 'Water Leakage Test (as per AAMA 501.2)' },
  { category: 'NDT Special Testing', title: 'Water Leakage Test (as per ASTM E1105)' },
  { category: 'NDT Special Testing', title: 'Air Infiltration Test (as per ASTM E783)' },
  { category: 'NDT Special Testing', title: 'Glass Sealant Adhesion Test' },
  { category: 'NDT Special Testing', title: 'Fire Stop Inspection Services' },
  { category: 'NDT Special Testing', title: 'Fire Stop Surveil Services (before/during/after application)' },
  { category: 'NDT Special Testing', title: 'Geomembrane Inspection Services' },
  { category: 'NDT Special Testing', title: 'Dry Roof Test' },
  { category: 'NDT Special Testing', title: 'Vacuum Box Test' },
  { category: 'NDT Special Testing', title: 'Spark Test' },
  { category: 'NDT Special Testing', title: 'NDT Borescope' },
];


function LocationMap({ location }) {
  if (!location) return <Typography variant="caption" color="text.secondary">No location data</Typography>;
  
  const parts = location.split(',');
  if (parts.length !== 2) return <Typography variant="caption" color="text.secondary">Invalid location format</Typography>;
  
  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);
  
  if (isNaN(lat) || isNaN(lng)) return <Typography variant="caption" color="text.secondary">Invalid coordinates</Typography>;

  return (
    <Box sx={{ height: 200, width: '100%', mt: 1, mb: 2, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
       <MapContainer center={[lat, lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <Marker position={[lat, lng]} />
       </MapContainer>
    </Box>
  );
}

function Datasheets() {
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedUserFilter, setSelectedUserFilter] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  // Edit Dialog State
  const [editOpen, setEditOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    id: '',
    project_id: '',
    assigned_to_id: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    type_of_work: '',
    location: '',
    client_id: '',
    lead_crm_id: '',
    client_followup_id: '',
    lead_followup_id: '',
    description: '',
    checkIn: '',
    checkOut: '',
    checkInLocation: '',
    checkOutLocation: ''
  });
  
  // Form State
  const [formData, setFormData] = useState({
    project_id: '',
    assigned_to_id: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    type_of_work: '',
    location: '',
    description: '',
    client_id: '',
    lead_crm_id: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  // In a real app, use Context for user info. Assuming user_id=1 for now or decoded from token in a real scenario.
  // For this demo, we'll let the backend handle the user_id from the token if possible, 
  // but the current API expects user_id in the body. I'll decode it or just use a placeholder if I can't.
  // The login response returns user_id, let's assume it's stored or we can get it.
  const userId = localStorage.getItem('user_id') || 1; 
  const role = localStorage.getItem('user_role') || ''; 

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Schedule
      try {
        const eventsRes = await axios.get(`${API_URL}/schedule`, { headers });
        const formattedEvents = eventsRes.data.map(e => {
          const currentUserId = parseInt(userId);
          let bgColor = '#3B82F6'; // Default Blue
          
          if (e.assigned_to_id) {
             if (e.assigned_to_id !== currentUserId) {
                 bgColor = '#ED6C02'; // Orange (Assigned to others)
             } else if (e.user_id !== currentUserId) {
                 bgColor = '#9C27B0'; // Purple (Assigned to me by others)
             }
          }

          return {
            id: e.id,
            title: `${e.project_name} - ${e.type_of_work || 'Work'}`,
            start: e.start,
            end: e.end,
            extendedProps: {
              description: e.description,
              type_of_work: e.type_of_work,
              location: e.location,
              project: e.project_name,
              project_id: e.project_id,
              project_latitude: e.project_latitude,
              project_longitude: e.project_longitude,
              assigned_to_id: e.assigned_to_id,
              assigned_to: e.assigned_to,
              user_id: e.user_id,
              user: e.user,
              client_id: e.client_id,
              lead_crm_id: e.lead_crm_id,
              client_followup_id: e.client_followup_id,
              lead_followup_id: e.lead_followup_id,
              check_in: e.check_in,
              check_out: e.check_out,
              check_in_location: e.check_in_location,
              check_out_location: e.check_out_location
            },
            backgroundColor: bgColor,
            borderColor: bgColor
          };
        });
        setEvents(formattedEvents);
      } catch (error) {
        console.error("Error fetching schedule:", error);
      }

      // Fetch Projects
      try {
        const projectsRes = await axios.get(`${API_URL}/projects`, { headers });
        console.log("Projects fetched:", projectsRes.data);
        setProjects(projectsRes.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }

      
      // Fetch Clients
      try {
        const clientsRes = await axios.get(`${API_URL}/clients`, { headers });
        setClients(clientsRes.data);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }

      // Fetch Leads
      try {
        const leadsRes = await axios.get(`${API_URL}/leads`, { headers });
        setLeads(leadsRes.data);
      } catch (error) {
        console.error("Error fetching leads:", error);
      }
      // Fetch Users
      try {
        const usersRes = await axios.get(`${API_URL}/setup/users`, { headers });
        setUsers(usersRes.data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }

    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 401) {
        alert("Session expired. Please login again.");
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getLocation = (callback) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const loc = `${position.coords.latitude}, ${position.coords.longitude}`;
        callback(loc);
      }, (error) => {
        console.error("Error getting location:", error);
        callback("Location unavailable");
      });
    } else {
      callback("Geolocation not supported");
    }
  };

  const filteredEvents = events.filter(event => {
        // User Filter
        let userMatch = true;
        if (selectedUserFilter.length > 0) {
            userMatch = selectedUserFilter.includes(event.extendedProps.user_id) || selectedUserFilter.includes(event.extendedProps.assigned_to_id);
        }

        // Type Filter
        let typeMatch = true;
        if (selectedTypeFilter.length > 0) {
            typeMatch = selectedTypeFilter.includes(event.extendedProps.type_of_work);
        }

        // Date Filter
        let dateMatch = true;
        if (filterStartDate && filterEndDate) {
            const eventDate = new Date(event.start).getTime();
            const sDate = new Date(filterStartDate).getTime();
            const eDate = new Date(filterEndDate).getTime() + 86400000; // include end of day
            dateMatch = eventDate >= sDate && eventDate <= eDate;
        } else if (filterStartDate) {
            const eventDate = new Date(event.start).getTime();
            const sDate = new Date(filterStartDate).getTime();
            dateMatch = eventDate >= sDate;
        } else if (filterEndDate) {
            const eventDate = new Date(event.start).getTime();
            const eDate = new Date(filterEndDate).getTime() + 86400000;
            dateMatch = eventDate <= eDate;
        }

        return userMatch && typeMatch && dateMatch;
    });

  const handleCreate = async () => {
    // Validation
    if (!formData.startDate || !formData.startTime) {
      alert("Please fill in start date and time.");
      return;
    }

    setCreateLoading(true);
    try {
      const payload = { ...formData };
      
      const response = await axios.post(`${API_URL}/schedule`, payload, { headers });
      setOpenAdd(false);
      setFormData({
        user_id: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        type_of_work: 'Remote',
        location: '',
        description: '',
        client_id: '',
        lead_crm_id: ''
      });
      fetchData();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Please check all fields.";
      alert(`Error creating entry: ${msg}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (selectedUserFilter.length > 0) params.append('users', selectedUserFilter.join(','));
      if (selectedTypeFilter.length > 0) params.append('types', selectedTypeFilter.join(','));

      const queryString = params.toString();
      const exportUrl = queryString ? `${API_URL}/schedule/export?${queryString}` : `${API_URL}/schedule/export`;

      const response = await axios.get(exportUrl, {
        headers,
        responseType: 'blob', // Important for file download
      });
      
      // Create a link to download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'timesheet_report.pdf');
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export report. Please try again.");
    }
  };

  const handleEventClick = (info) => {
    const event = info.event;
    const start = new Date(event.start);
    const end = new Date(event.end);
    
    // Helper to format date as YYYY-MM-DD
    const formatDate = (d) => d.toISOString().split('T')[0];
    // Helper to format time as HH:MM
    const formatTime = (d) => d.toTimeString().split(' ')[0].substring(0, 5);

    // Helper for datetime-local
    const formatDateTime = (d) => {
        if (!d) return '';
        const pad = (num) => (num < 10 ? '0' : '') + num;
        return (
            d.getFullYear() +
            '-' +
            pad(d.getMonth() + 1) +
            '-' +
            pad(d.getDate()) +
            'T' +
            pad(d.getHours()) +
            ':' +
            pad(d.getMinutes())
        );
    };

    const checkIn = event.extendedProps.check_in ? new Date(event.extendedProps.check_in) : null;
    const checkOut = event.extendedProps.check_out ? new Date(event.extendedProps.check_out) : null;

    setEditFormData({
        id: event.id,
        project_id: event.extendedProps.project_id || '',
        assigned_to_id: event.extendedProps.assigned_to_id || '',
        startDate: formatDate(start),
        startTime: formatTime(start),
        endDate: end ? formatDate(end) : '',
        endTime: end ? formatTime(end) : '',
        type_of_work: event.extendedProps.type_of_work,
        location: event.extendedProps.location || '',
        description: event.extendedProps.description,
        client_id: event.extendedProps.client_id || '',
        lead_crm_id: event.extendedProps.lead_crm_id || '',
        client_followup_id: event.extendedProps.client_followup_id || '',
        lead_followup_id: event.extendedProps.lead_followup_id || '',
        checkIn: checkIn ? formatDateTime(checkIn) : '',
        checkOut: checkOut ? formatDateTime(checkOut) : '',
        checkInLocation: event.extendedProps.check_in_location || '',
        checkOutLocation: event.extendedProps.check_out_location || ''
    });
    setEditOpen(true);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editId = searchParams.get('editId');
    const openEventId = location.state?.openEventId;
    
    const targetId = editId ? parseInt(editId) : (openEventId ? parseInt(openEventId) : null);

    if (targetId && events.length > 0) {
        const event = events.find(e => e.id === targetId);
        if (event) {
             // Only open if not already open to avoid loops/flickering
             if (!editOpen) {
                handleEventClick({ event: event });
                // Clear the trigger so it doesn't persist
                if (editId) {
                    navigate(location.pathname, { replace: true });
                } else if (openEventId) {
                    // Clear state
                    navigate(location.pathname, { replace: true, state: {} });
                }
             }
        }
    }
  }, [events, location.search, location.state, editOpen, navigate, location.pathname]);

  const handleUpdateEvent = async () => {
    if (!editFormData.startDate || !editFormData.startTime) {
        alert("Please fill in start date and time.");
        return;
    }

    const start = `${editFormData.startDate}T${editFormData.startTime}`;
    const end = editFormData.endDate && editFormData.endTime ? `${editFormData.endDate}T${editFormData.endTime}` : null;
    
    const check_in = editFormData.checkIn || null;
    const check_out = editFormData.checkOut || null;

    try {
        await axios.put(`${API_URL}/schedule/${editFormData.id}`, {
            project_id: editFormData.project_id,
            assigned_to_id: editFormData.assigned_to_id,
            start,
            end,
            type_of_work: editFormData.type_of_work,
            location: editFormData.location,
            description: editFormData.description,
            client_id: editFormData.client_id,
            lead_crm_id: editFormData.lead_crm_id,
            check_in,
            check_out,
            check_in_location: editFormData.checkInLocation,
            check_out_location: editFormData.checkOutLocation
        }, { headers });
        
        setEditOpen(false);
        fetchData();
    } catch (err) {
        console.error(err);
        alert("Failed to update event");
    }
  };

  const handleDeleteEvent = async () => {
      if (!window.confirm("Are you sure you want to delete this entry?")) return;
      
      try {
          await axios.delete(`${API_URL}/schedule/${editFormData.id}`, { headers });
          setEditOpen(false);
          fetchData();
      } catch (err) {
          console.error(err);
          alert("Failed to delete event");
      }
  };

  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const isCheckedIn = event.extendedProps.check_in;
    const isCheckedOut = event.extendedProps.check_out;
    const assignedTo = event.extendedProps.assigned_to;
    const createdBy = event.extendedProps.user;
    const currentUserId = parseInt(userId);
    const assignedToId = event.extendedProps.assigned_to_id;
    const creatorId = event.extendedProps.user_id;
    const checkInLocation = event.extendedProps.check_in_location;
    const projectLat = event.extendedProps.project_latitude;
    const projectLng = event.extendedProps.project_longitude;

    // Determine Status Color and Background
    let statusColor = event.backgroundColor; 
    let bgColor = '#ffffff'; // Default white
    let borderColor = '#e0e0e0';

    if (isCheckedOut) {
        statusColor = '#2e7d32'; // Darker Green
        bgColor = '#f1f8e9'; // Very Light Green
        borderColor = '#c5e1a5';
    } else if (isCheckedIn) {
        statusColor = '#1565c0'; // Darker Blue
        bgColor = '#e3f2fd'; // Very Light Blue
        borderColor = '#90caf9';
    } else {
        statusColor = '#ef6c00'; // Darker Orange
        bgColor = '#fff3e0'; // Very Light Orange
        borderColor = '#ffcc80';
    }

    let locationStatus = null;
    if (checkInLocation && projectLat && projectLng) {
        const parts = checkInLocation.split(',');
        if (parts.length === 2) {
            const checkInLat = parseFloat(parts[0]);
            const checkInLng = parseFloat(parts[1]);
            const distance = calculateDistance(checkInLat, checkInLng, projectLat, projectLng);
            
            if (distance !== null) {
                const isNear = distance < 0.5; // 500 meters
                locationStatus = (
                    <Tooltip title={isNear ? "On Site" : `Off Site (${distance.toFixed(2)} km)`}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            bgcolor: isNear ? 'rgba(46, 125, 50, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                            color: isNear ? 'success.dark' : 'error.dark',
                            px: 0.5, 
                            borderRadius: 10,
                            border: `1px solid ${isNear ? 'rgba(46, 125, 50, 0.3)' : 'rgba(211, 47, 47, 0.3)'}`,
                            fontSize: '0.6rem',
                            fontWeight: 'bold',
                            ml: 'auto',
                            height: 18
                        }}>
                            <LocationOnIcon sx={{ fontSize: 10, mr: 0.2 }} />
                            {isNear ? 'On Site' : `${distance.toFixed(1)}km`}
                        </Box>
                    </Tooltip>
                );
            }
        }
    }
    
    return (
      <Box sx={{ 
        p: 0.8, 
        width: '100%', 
        overflow: 'hidden',
        bgcolor: bgColor,
        border: `1px solid ${borderColor}`,
        borderLeft: `3px solid ${statusColor}`,
        color: 'text.primary',
        borderRadius: 1.5,
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        mb: 0.5,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, zIndex: 10 }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem', lineHeight: 1.2 }} noWrap>
                {event.extendedProps.project || 'No Project'}
            </Typography>
        </Box>
        
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }} noWrap>
            {event.extendedProps.type_of_work || 'Work Log'}
        </Typography>

        <Box sx={{ mb: 0.5 }}>
             <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem', color: 'text.secondary' }} noWrap>
                <Box component="span" sx={{ fontWeight: 'bold' }}>By:</Box> {createdBy}
             </Typography>
             {assignedTo && (
                 <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem', color: 'primary.main' }} noWrap>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>To:</Box> {assignedTo}
                 </Typography>
             )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 'medium' }}>
                {eventInfo.timeText}
            </Typography>
            {locationStatus}
        </Box>
      </Box>
    );
  };

  if (loading) return <Loading message="Loading Task List..." />;

  const _currentUserId = parseInt(userId);
  const availableUsers = users.filter(u => {
      if (role === 'Admin') return true;
      if (role === 'User') return u.id === _currentUserId;
      return u.id === _currentUserId || u.manager_id === _currentUserId;
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grow in={true} timeout={500}>
      {/* Header Section */}
      <Paper elevation={0} sx={{  
        p: { xs: 3, md: 5 }, 
        mb: 4, 
        borderRadius: 4, 
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(30, 60, 114, 0.2)'
      }}>
        {/* Background Decorative Circles */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: 100, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-start' }, flexWrap: 'wrap', gap: 3 }}>
                {/* Title & Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2, width: '100%' }}>
                    <Typography variant="h5" fontWeight="800" sx={{ color: 'white', letterSpacing: 0.3 }}>
                        Task List
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                        <Button 
                            variant="contained" 
                            onClick={handleExport} 
                            startIcon={<PictureAsPdfIcon />}
                            fullWidth
                            sx={{ 
                                bgcolor: 'rgba(255,255,255,0.2)', 
                                backdropFilter: 'blur(10px)',
                                color: 'white', 
                                boxShadow: 'none',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                flex: { xs: 1, sm: 'initial' }
                            }}
                        >
                            Export
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={() => setOpen(true)} 
                            startIcon={<AddIcon />}
                            fullWidth
                            sx={{ 
                                bgcolor: 'white', 
                                color: 'primary.main',
                                fontWeight: 'bold',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                flex: { xs: 1, sm: 'initial' }
                            }}
                        >
                            Add
                        </Button>
                    </Box>
                </Box>

                {/* Actions & Filters */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        type="date"
                        label="Start Date"
                        InputLabelProps={{ shrink: true, sx: { color: 'rgba(255,255,255,0.7)' } }}
                        size="small"
                        value={filterStartDate}
                        onChange={(e) => setFilterStartDate(e.target.value)}
                        sx={{ 
                            minWidth: 150,
                            input: { color: 'white' },
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(255,255,255,0.15)',
                                borderRadius: 3,
                                backdropFilter: 'blur(10px)',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'white' },
                                '&.Mui-focused fieldset': { borderColor: 'white' }
                            }
                        }}
                    />
                    <TextField
                        type="date"
                        label="End Date"
                        InputLabelProps={{ shrink: true, sx: { color: 'rgba(255,255,255,0.7)' } }}
                        size="small"
                        value={filterEndDate}
                        onChange={(e) => setFilterEndDate(e.target.value)}
                        sx={{ 
                            minWidth: 150,
                            input: { color: 'white' },
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(255,255,255,0.15)',
                                borderRadius: 3,
                                backdropFilter: 'blur(10px)',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'white' },
                                '&.Mui-focused fieldset': { borderColor: 'white' }
                            }
                        }}
                    />

                    <Autocomplete
                        multiple
                        size="small"
                        options={availableUsers}
                        getOptionLabel={(option) => option.full_name}
                        value={availableUsers.filter(u => selectedUserFilter.includes(u.id))}
                        onChange={(e, newValue) => setSelectedUserFilter(newValue.map(v => v.id))}
                        sx={{ 
                            minWidth: { xs: '100%', sm: 220 },
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(255,255,255,0.15)',
                                color: 'white',
                                borderRadius: 3,
                                backdropFilter: 'blur(10px)',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'white' },
                                '&.Mui-focused fieldset': { borderColor: 'white' }
                            },
                            '& .MuiChip-root': { bgcolor: 'rgba(255,255,255,0.2)', color: 'white' },
                            '& .MuiSvgIcon-root': { color: 'white' }
                        }}
                        renderInput={(params) => <TextField {...params} placeholder="Filter by User..." />}
                    />

                    <Autocomplete
                        multiple
                        size="small"
                        options={WORK_TYPES.map(w => w.title)}
                        value={selectedTypeFilter}
                        onChange={(e, newValue) => setSelectedTypeFilter(newValue)}
                        sx={{ 
                            minWidth: { xs: '100%', sm: 220 },
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(255,255,255,0.15)',
                                color: 'white',
                                borderRadius: 3,
                                backdropFilter: 'blur(10px)',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'white' },
                                '&.Mui-focused fieldset': { borderColor: 'white' }
                            },
                            '& .MuiChip-root': { bgcolor: 'rgba(255,255,255,0.2)', color: 'white' },
                            '& .MuiSvgIcon-root': { color: 'white' }
                        }}
                        renderInput={(params) => <TextField {...params} placeholder="Filter by Type of Work..." />}
                    />
                </Box>
            </Box>
        </Box>
      </Paper>
      </Grow>

      <Grow in={true} timeout={800}>
      <Grid container spacing={3}>
        {/* Calendar Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ 
              p: 2, 
              borderRadius: 4, 
              height: '100%', 
              bgcolor: '#ffffff', 
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)', 
              border: '1px solid #e2e8f0',
              '& .fc': {
                  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
              },
              '& .fc-header-toolbar': {
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                  m: '-16px -16px 16px -16px', // Pull it to the edges of the padding
                  p: 2.5,
                  borderRadius: '16px 16px 0 0',
                  color: 'white'
              },
              '& .fc-toolbar-title': {
                  color: 'white !important', 
                  fontWeight: '800 !important',
                  letterSpacing: '0.5px'
              },
              '& .fc-button-primary': {
                  bgcolor: 'rgba(255,255,255,0.15) !important',
                  borderColor: 'rgba(255,255,255,0.2) !important',
                  color: 'white !important',
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                  borderRadius: '8px',
                  boxShadow: 'none !important',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25) !important'
                  },
                  '&:active, &.fc-button-active': {
                      bgcolor: 'white !important',
                      color: '#1e3c72 !important'
                  }
              },
              '& .fc-theme-standard th': {
                  bgcolor: '#f8fafc',
                  color: '#64748b',
                  py: 1.5,
                  fontWeight: '800',
                  textTransform: 'uppercase',
                  fontSize: '0.8rem',
                  border: 'none',
                  borderBottom: '2px solid #e2e8f0'
              },
              '& .fc-theme-standard td': {
                  border: '1px solid #f1f5f9'
              },
              '& .fc-day-today': {
                  bgcolor: 'rgba(30, 60, 114, 0.04) !important'
              },
              '& .fc-event': {
                  borderRadius: '6px',
                  border: 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'transform 0.1s',
                  '&:hover': {
                      transform: 'scale(1.02)'
                  }
              }
          }}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
              }}
              initialView="dayGridMonth"
              events={filteredEvents}
              height="75vh"
              eventClick={handleEventClick}
              eventContent={renderEventContent}
              dayMaxEvents={true}
              eventDisplay="block"
              views={{
                  dayGridMonth: {
                      dayMaxEvents: 2
                  }
              }}
            />
          </Paper>
        </Grid>

        {/* Recent / Summary Section */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 4, borderRadius: 4, height: '100%', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 2, borderBottom: '2px solid #e2e8f0' }}>
              <Typography variant="h6" fontWeight="800" sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIndIcon /> Recent Entries
              </Typography>
              <IconButton size="small" onClick={fetchData} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', '&:hover': { bgcolor: '#f1f5f9' } }}>
                  <RefreshIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {filteredEvents.slice(0, 5).map((event) => {
                const checkInLocation = event.extendedProps.check_in_location;
                const projectLat = event.extendedProps.project_latitude;
                const projectLng = event.extendedProps.project_longitude;
                let locationStatus = null;
                
                if (checkInLocation && projectLat && projectLng) {
                    const parts = checkInLocation.split(',');
                    if (parts.length === 2) {
                        const checkInLat = parseFloat(parts[0]);
                        const checkInLng = parseFloat(parts[1]);
                        const distance = calculateDistance(checkInLat, checkInLng, projectLat, projectLng);
                        
                        if (distance !== null) {
                            const isNear = distance < 0.5;
                            locationStatus = (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: isNear ? '#4caf50' : '#f44336' }} />
                                    <Typography variant="caption" color={isNear ? 'success.main' : 'error.main'}>
                                        {isNear ? 'On Site' : `Off Site (${distance.toFixed(2)} km)`}
                                    </Typography>
                                </Box>
                            );
                        }
                    }
                }

                return (
                <Card 
                  key={event.id} 
                  variant="outlined" 
                  sx={{ 
                      borderRadius: 3, 
                      cursor: 'pointer', 
                      bgcolor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                          boxShadow: '0 8px 24px rgba(0,0,0,0.06)', 
                          transform: 'translateY(-2px)',
                          borderColor: 'primary.main'
                      } 
                  }}
                  onClick={() => handleEventClick({ event: event })}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Typography variant="subtitle2" color="primary.main" fontWeight="800" gutterBottom>
                      {event.extendedProps.project}
                    </Typography>
                    <Typography variant="body1" fontWeight="600" sx={{ color: 'text.primary', mb: 1 }}>
                      {event.extendedProps.type_of_work || 'Work Log'}
                    </Typography>
                    <Box sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, mt: 1.5 }}>
                        {locationStatus}
                        {event.extendedProps.assigned_to && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#64748b', fontWeight: 'bold' }}>
                                <PersonIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                {event.extendedProps.assigned_to}
                            </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', mt: 1, fontWeight: 'bold' }}>
                          <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          {new Date(event.start).toLocaleString()}
                        </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )})}
              {filteredEvents.length === 0 && (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No entries found.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      </Grow>

      {/* Add Entry Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
            sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }
        }}
      >
        <DialogTitle sx={{ 
            fontWeight: '800', 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            p: 3
        }}>
            <AddIcon /> Add New Datasheet Entry
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4, bgcolor: '#f8fafc' }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>PROJECT & ASSIGNMENT</Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'white' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                          options={projects}
                          getOptionLabel={(option) => {
                              const label = option.name || "";
                              return option.ref ? `${label} (${option.ref})` : label;
                          }}
                          value={projects.find(p => p.id === formData.project_id) || null}
                          onChange={(event, newValue) => {
                              setFormData({ ...formData, project_id: newValue ? newValue.id : '' });
                          }}
                          renderInput={(params) => <TextField {...params} label="Project" fullWidth variant="outlined" />}
                        />
                    </Grid>

                    {(role.toLowerCase() === 'admin' || role.toLowerCase() === 'manager') && (
                    <Grid item xs={12}>
                        <Autocomplete
                          options={users}
                          getOptionLabel={(option) => {
                              const label = option.full_name || option.username || "";
                              return `${label} (${option.username})`;
                          }}
                          value={users.find(u => u.id === formData.assigned_to_id) || null}
                          onChange={(event, newValue) => {
                              setFormData({ ...formData, assigned_to_id: newValue ? newValue.id : '' });
                          }}
                          renderInput={(params) => <TextField {...params} label="Assign To" placeholder="Myself" fullWidth variant="outlined" />}
                        />
                    </Grid>
                    )}
                </Grid>
              </Paper>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>TIME FRAME</Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'white' }}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Start Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                          label="Start Time"
                          type="time"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={formData.startTime}
                          onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label="End Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                          label="End Time"
                          type="time"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={formData.endTime}
                          onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      />
                    </Grid>
                </Grid>
              </Paper>
            </Box>

            <Box>
               <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>WORK DETAILS</Typography>
               <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'white' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        freeSolo
                        options={WORK_TYPES}
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.title}
                        inputValue={formData.type_of_work}
                        onInputChange={(event, newInputValue) => {
                            setFormData({ ...formData, type_of_work: newInputValue });
                        }}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Type of Work" 
                                fullWidth 
                                placeholder="e.g. Visual Inspection VT"
                            />
                        )}
                      />
                    </Grid>

                    {formData.type_of_work?.includes('Follow') && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Autocomplete
                          options={clients}
                          getOptionLabel={(option) => option.name || ""}
                          value={clients.find(c => c.id === formData.client_id) || null}
                          onChange={(e, val) => setFormData({...formData, client_id: val ? val.id : '', lead_crm_id: ''})}
                          disabled={!!formData.lead_crm_id}
                          renderInput={(params) => <TextField {...params} label="Link to Client" fullWidth />}
                          sx={{ flex: 1 }}
                        />
                        <Typography sx={{ alignSelf: 'center', fontWeight: 'bold', color: 'text.secondary' }}>OR</Typography>
                        <Autocomplete
                          options={leads}
                          getOptionLabel={(option) => option.company_name || option.contact_person || option.crm_guid || ""}
                          value={leads.find(l => l.crm_guid === formData.lead_crm_id) || null}
                          onChange={(e, val) => setFormData({...formData, lead_crm_id: val ? val.crm_guid : '', client_id: ''})}
                          disabled={!!formData.client_id}
                          renderInput={(params) => <TextField {...params} label="Link to Lead" fullWidth />}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Grid>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        label="Location"
                        fullWidth
                        placeholder="e.g. Office, Home, Client Site"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </Grid>
                </Grid>
               </Paper>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold' }}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={createLoading} sx={{ borderRadius: 2, px: 4, py: 1, fontWeight: 'bold' }}>
            {createLoading ? 'Saving...' : 'Save Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog 
        open={editOpen} 
        onClose={() => setEditOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
            sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }
        }}
      >
        <DialogTitle sx={{ 
            fontWeight: '800', 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: 'white',
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            p: 3
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CalendarTodayIcon /> Edit Datasheet Entry
            </Box>
            {(editFormData.client_id || editFormData.lead_crm_id) && (
                <Button 
                    variant="contained" 
                    size="small" 
                    onClick={() => {
                        setEditOpen(false);
                        if (editFormData.client_id) {
                            navigate(`/clients/${editFormData.client_id}`);
                        } else if (editFormData.lead_crm_id) {
                            navigate(`/leads`);
                        }
                    }}
                    sx={{ bgcolor: 'white', color: '#1e3c72', '&:hover': { bgcolor: '#f1f5f9' }, fontWeight: 'bold' }}
                >
                    Go to {editFormData.client_id ? 'Client' : 'Lead'}
                </Button>
            )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4, bgcolor: '#f8fafc' }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>PROJECT & ASSIGNMENT</Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'white' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                          options={projects}
                          getOptionLabel={(option) => {
                              const label = option.name || "";
                              return option.ref ? `${label} (${option.ref})` : label;
                          }}
                          value={projects.find(p => p.id === editFormData.project_id) || null}
                          onChange={(event, newValue) => {
                              setEditFormData({ ...editFormData, project_id: newValue ? newValue.id : '' });
                          }}
                          renderInput={(params) => <TextField {...params} label="Project" fullWidth variant="outlined" />}
                        />
                    </Grid>

                    {(role.toLowerCase() === 'admin' || role.toLowerCase() === 'manager') && (
                    <Grid item xs={12}>
                        <Autocomplete
                          options={users}
                          getOptionLabel={(option) => {
                              const label = option.full_name || option.username || "";
                              return `${label} (${option.username})`;
                          }}
                          value={users.find(u => u.id === editFormData.assigned_to_id) || null}
                          onChange={(event, newValue) => {
                              setEditFormData({ ...editFormData, assigned_to_id: newValue ? newValue.id : '' });
                          }}
                          renderInput={(params) => <TextField {...params} label="Assign To" placeholder="Myself" fullWidth variant="outlined" />}
                        />
                    </Grid>
                    )}
                </Grid>
              </Paper>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>TIME FRAME</Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'white' }}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Start Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={editFormData.startDate}
                        onChange={(e) => setEditFormData({ ...editFormData, startDate: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                          label="Start Time"
                          type="time"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={editFormData.startTime}
                          onChange={(e) => setEditFormData({ ...editFormData, startTime: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label="End Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={editFormData.endDate}
                        onChange={(e) => setEditFormData({ ...editFormData, endDate: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                          label="End Time"
                          type="time"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={editFormData.endTime}
                          onChange={(e) => setEditFormData({ ...editFormData, endTime: e.target.value })}
                      />
                    </Grid>
                </Grid>
              </Paper>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>ATTENDANCE TRACKING</Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'white' }}>
                <Grid container spacing={2}>
                    {/* Check In Section */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            label="Check In"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{ shrink: true, readOnly: true }}
                            value={editFormData.checkIn}
                            size="small"
                        />
                        <Button 
                            variant="contained" 
                            color="success"
                            onClick={() => {
                                const now = new Date();
                                const pad = (num) => (num < 10 ? '0' : '') + num;
                                const localISOTime = (
                                    now.getFullYear() +
                                    '-' +
                                    pad(now.getMonth() + 1) +
                                    '-' +
                                    pad(now.getDate()) +
                                    'T' +
                                    pad(now.getHours()) +
                                    ':' +
                                    pad(now.getMinutes())
                                );
                                
                                getLocation((loc) => {
                                    setEditFormData(prev => ({
                                        ...prev,
                                        checkIn: localISOTime,
                                        checkInLocation: loc
                                    }));
                                });
                            }}
                            sx={{ borderRadius: 2, fontWeight: 'bold' }}
                        >
                            IN
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '4px !important' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold">IN LOCATION</Typography>
                            {(() => {
                                if (editFormData.checkInLocation && editFormData.project_id) {
                                    const project = projects.find(p => p.id === editFormData.project_id);
                                    if (project && project.latitude && project.longitude) {
                                        const parts = editFormData.checkInLocation.split(',');
                                        if (parts.length === 2) {
                                            const lat = parseFloat(parts[0]);
                                            const lng = parseFloat(parts[1]);
                                            const dist = calculateDistance(lat, lng, project.latitude, project.longitude);
                                            if (dist !== null) {
                                                const isNear = dist < 0.5;
                                                return (
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: isNear ? '#4caf50' : '#f44336' }} />
                                                        <Typography variant="caption" color={isNear ? 'success.main' : 'error.main'} fontWeight="bold">
                                                            {isNear ? 'On Site' : `Off Site (${dist.toFixed(2)} km)`}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }
                                        }
                                    }
                                }
                                return null;
                            })()}
                        </Box>
                        <LocationMap location={editFormData.checkInLocation} />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    {/* Check Out Section */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            label="Check Out"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{ shrink: true, readOnly: true }}
                            value={editFormData.checkOut}
                            size="small"
                        />
                        <Button 
                            variant="contained" 
                            color="error"
                            onClick={() => {
                                const now = new Date();
                                const pad = (num) => (num < 10 ? '0' : '') + num;
                                const localISOTime = (
                                    now.getFullYear() +
                                    '-' +
                                    pad(now.getMonth() + 1) +
                                    '-' +
                                    pad(now.getDate()) +
                                    'T' +
                                    pad(now.getHours()) +
                                    ':' +
                                    pad(now.getMinutes())
                                );

                                getLocation((loc) => {
                                    setEditFormData(prev => ({
                                        ...prev,
                                        checkOut: localISOTime,
                                        checkOutLocation: loc
                                    }));
                                });
                            }}
                            sx={{ borderRadius: 2, fontWeight: 'bold' }}
                        >
                            OUT
                        </Button>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '4px !important' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold">OUT LOCATION</Typography>
                            {(() => {
                                if (editFormData.checkOutLocation && editFormData.project_id) {
                                    const project = projects.find(p => p.id === editFormData.project_id);
                                    if (project && project.latitude && project.longitude) {
                                        const parts = editFormData.checkOutLocation.split(',');
                                        if (parts.length === 2) {
                                            const lat = parseFloat(parts[0]);
                                            const lng = parseFloat(parts[1]);
                                            const dist = calculateDistance(lat, lng, project.latitude, project.longitude);
                                            if (dist !== null) {
                                                const isNear = dist < 0.5;
                                                return (
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: isNear ? '#4caf50' : '#f44336' }} />
                                                        <Typography variant="caption" color={isNear ? 'success.main' : 'error.main'} fontWeight="bold">
                                                            {isNear ? 'On Site' : `Off Site (${dist.toFixed(2)} km)`}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }
                                        }
                                    }
                                }
                                return null;
                            })()}
                        </Box>
                        <LocationMap location={editFormData.checkOutLocation} />
                    </Grid>
                </Grid>
              </Paper>
            </Box>

            <Box>
               <Typography variant="overline" color="text.secondary" fontWeight="800" sx={{ mb: 1, display: 'block' }}>WORK DETAILS</Typography>
               <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'white' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        freeSolo
                        options={WORK_TYPES}
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.title}
                        inputValue={editFormData.type_of_work}
                        onInputChange={(event, newInputValue) => {
                            setEditFormData({ ...editFormData, type_of_work: newInputValue });
                        }}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Type of Work" 
                                fullWidth 
                                placeholder="e.g. Visual Inspection VT"
                            />
                        )}
                      />
                    </Grid>

                    {editFormData.type_of_work?.includes('Follow') && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Autocomplete
                          options={clients}
                          getOptionLabel={(option) => option.name || ""}
                          value={clients.find(c => c.id === editFormData.client_id) || null}
                          onChange={(e, val) => setEditFormData({...editFormData, client_id: val ? val.id : '', lead_crm_id: ''})}
                          disabled={!!editFormData.lead_crm_id}
                          renderInput={(params) => <TextField {...params} label="Link to Client" fullWidth />}
                          sx={{ flex: 1 }}
                        />
                        <Typography sx={{ alignSelf: 'center', fontWeight: 'bold', color: 'text.secondary' }}>OR</Typography>
                        <Autocomplete
                          options={leads}
                          getOptionLabel={(option) => option.company_name || option.contact_person || option.crm_guid || ""}
                          value={leads.find(l => l.crm_guid === editFormData.lead_crm_id) || null}
                          onChange={(e, val) => setEditFormData({...editFormData, lead_crm_id: val ? val.crm_guid : '', client_id: ''})}
                          disabled={!!editFormData.client_id}
                          renderInput={(params) => <TextField {...params} label="Link to Lead" fullWidth />}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Grid>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        label="Location"
                        fullWidth
                        placeholder="e.g. Office, Home, Client Site"
                        value={editFormData.location}
                        onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        value={editFormData.description}
                        onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      />
                    </Grid>
                </Grid>
               </Paper>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
          <Button onClick={handleDeleteEvent} color="error" sx={{ fontWeight: 'bold' }}>Delete</Button>
          <Box>
            <Button onClick={() => setEditOpen(false)} sx={{ color: 'text.secondary', fontWeight: 'bold', mr: 2 }}>Cancel</Button>
            <Button onClick={handleUpdateEvent} variant="contained" sx={{ borderRadius: 2, px: 4, py: 1, fontWeight: 'bold' }}>Update Entry</Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Datasheets;
